// Résumé d'une analyse TURPE dans la liste « PDL connectables — Acheminement TURPE »
// (demande d'Adrien du 23/10/2026, PDL BAO FAMILY) : coche « analysé », PS actuelle →
// proposée, version d'utilisation (FTA) actuelle → proposée, économie €/an, rapport PDF.
// Les chiffres viennent du backend (utils/turpeAnalyseResume.js), mêmes règles que le PDF.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FiCheckCircle, FiDownload, FiLoader, FiAlertTriangle } from 'react-icons/fi';
import { lireReponseApi } from '../../utils/reponseApiTurpe';

const CADRANS = ['pointe', 'hph', 'hch', 'hpb', 'hcb'];

const euros = (v) => (v == null || !Number.isFinite(Number(v))
  ? '—'
  : `${Math.round(Number(v)).toLocaleString('fr-FR')} €`);

const dateCourte = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};

/**
 * Résumé à afficher pour l'année choisie ; à défaut le plus récent (signalé).
 * `analyses` : null = inconnu (lecture en échec), [] = jamais analysé.
 */
export function choisirResume(analyses, annee) {
  if (!Array.isArray(analyses) || analyses.length === 0) return null;
  const memeAnnee = analyses.find((a) => a.annee === annee);
  if (memeAnnee) return { resume: memeAnnee, autreAnnee: false };
  return { resume: analyses[0], autreAnnee: true };
}

/** « 96 kVA » si tous les cadrans sont égaux, sinon « HPH 96 · HCH 96 … ». */
export function libellePs(ps, unite) {
  if (!ps) return '?';
  const valeurs = CADRANS.filter((c) => ps[c] != null).map((c) => [c, ps[c]]);
  if (!valeurs.length) return '?';
  const fmt = (v) => Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 });
  if (new Set(valeurs.map(([, v]) => v)).size === 1) return `${fmt(valeurs[0][1])} ${unite || ''}`.trim();
  return valeurs.map(([c, v]) => `${c.toUpperCase()} ${fmt(v)}`).join(' · ');
}

/** Valeur unique si tous les cadrans sont égaux, sinon null. */
function valeurUnique(ps) {
  if (!ps) return null;
  const valeurs = CADRANS.filter((c) => ps[c] != null).map((c) => ps[c]);
  return valeurs.length && new Set(valeurs).size === 1 ? valeurs[0] : null;
}

/** Statut d'une économie qui n'est pas une économie (règle du 24/10/2026). */
const LIBELLE_STATUT = { non_mesure: 'non mesurée', surcout: 'surcoût — non retenue' };

/** Morceaux « PS 96 → 70 kVA » et « FTA CU → LU » (vides si aucune proposition connue). */
export function morceauxOptimisation(resume) {
  const sortie = { ps: '', fta: '' };
  if (!resume || resume.statut !== 'concluant') return sortie;
  const { ps, fta } = resume;
  if (ps?.proposee) {
    const u1 = valeurUnique(ps.actuelle);
    const u2 = valeurUnique(ps.proposee);
    const fmt = (v) => Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 });
    if (u1 != null && u2 != null) {
      sortie.ps = u1 === u2 ? `PS ${fmt(u2)} ${ps.unite || ''} (inchangée)`.replace('  ', ' ').trim()
        : `PS ${fmt(u1)} → ${fmt(u2)} ${ps.unite || ''}`.trim();
    } else {
      sortie.ps = `PS ${libellePs(ps.actuelle, ps.unite)} → ${libellePs(ps.proposee, ps.unite)}`;
    }
  }
  if (fta?.proposee) {
    sortie.fta = fta.changement ? `FTA ${fta.actuelle} → ${fta.proposee}` : `FTA ${fta.proposee} (inchangée)`;
    if (LIBELLE_STATUT[fta.statut]) sortie.fta += ` (${LIBELLE_STATUT[fta.statut]})`;
  }
  if (sortie.ps && LIBELLE_STATUT[ps?.statut]) sortie.ps += ` (${LIBELLE_STATUT[ps.statut]})`;
  return sortie;
}

/** Ligne « PS 96 → 70 kVA · FTA CU → LU » (rien si aucune proposition connue). */
export function libelleOptimisation(resume) {
  const { ps, fta } = morceauxOptimisation(resume);
  return [ps, fta].filter(Boolean).join(' · ');
}

/** Coche « analysé » + date (titre). `analyses` null = lecture en échec. */
export function CocheAnalyse({ analyses, annee }) {
  if (analyses === null) return <span className="text-[9px] text-amber-600" title="Analyses TURPE illisibles">?</span>;
  const choix = choisirResume(analyses, annee);
  if (!choix) return <span className="text-[9px] text-gray-300" data-testid="turpe-non-analyse">—</span>;
  const { resume, autreAnnee } = choix;
  if (resume.statut !== 'concluant') {
    const titreNc = `${resume.statut === 'non_verifie' ? 'Analyse non vérifiée' : 'Analyse non concluante'} (année ${resume.annee}) : ${(resume.motifs || []).join(' ')}`;
    return (
      <span className="inline-flex items-center gap-0.5 text-amber-600" title={titreNc} data-testid="turpe-non-concluant">
        <FiAlertTriangle className="w-3 h-3" aria-label="Non concluant" />
        {autreAnnee && <span className="text-[9px] text-gray-400">{resume.annee}</span>}
      </span>
    );
  }
  const titre = `Analysé le ${dateCourte(resume.analyse_le)} (année ${resume.annee})${resume.choix_valide ? ' — choix validé' : ' — choix non validé'}`;
  return (
    <span className="inline-flex items-center gap-0.5" title={titre} data-testid="turpe-analyse">
      <FiCheckCircle className={`w-3 h-3 ${autreAnnee ? 'text-gray-400' : 'text-emerald-600'}`} aria-label="Analysé" />
      {autreAnnee && <span className="text-[9px] text-gray-400">{resume.annee}</span>}
    </span>
  );
}

/** Bouton de téléchargement du dernier rapport PDF de l'analyse retenue. */
export function BoutonRapportTurpe({ rapport, session }) {
  const [enCours, setEnCours] = useState(false);
  if (!rapport?.id) return null;
  const telecharger = async (e) => {
    e.stopPropagation();
    setEnCours(true);
    try {
      const reponse = await fetch(`/api/turpe/reports/${rapport.id}/url`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const corps = await lireReponseApi(reponse, 'Rapport TURPE');
      const url = corps?.data?.url;
      if (!url) throw new Error('Rapport TURPE : lien absent de la réponse');
      const fichier = await fetch(url);
      if (!fichier.ok) throw new Error(`Rapport TURPE : téléchargement refusé (HTTP ${fichier.status})`);
      const blob = await fichier.blob();
      const lien = document.createElement('a');
      lien.href = URL.createObjectURL(blob);
      lien.download = rapport.nom || corps.data.file_name || 'Rapport_TURPE.pdf';
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      setTimeout(() => URL.revokeObjectURL(lien.href), 10000);
    } catch (err) {
      console.error('[BoutonRapportTurpe]', err);
      toast.error(err.message || 'Rapport TURPE indisponible');
    } finally {
      setEnCours(false);
    }
  };
  return (
    <button
      type="button"
      onClick={telecharger}
      disabled={enCours}
      title={`Télécharger ${rapport.nom || 'le rapport'}${rapport.genere_le ? ` (généré le ${dateCourte(rapport.genere_le)})` : ''}`}
      aria-label="Télécharger le rapport TURPE"
      data-testid="turpe-rapport"
      className="inline-flex items-center justify-center w-5 h-5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
    >
      {enCours ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiDownload className="w-3 h-3" />}
    </button>
  );
}

/** Cellules « Optimisation proposée » et « Économie / an » d'un PDL. */
export function CellulesOptimisation({ analyses, annee }) {
  const choix = choisirResume(analyses, annee);
  if (!choix) {
    return (
      <>
        <td className="px-2 py-1.5 text-[9px] text-gray-300">—</td>
        <td className="px-2 py-1.5 text-[9px] text-gray-300 text-right">—</td>
      </>
    );
  }
  const { resume } = choix;
  const morceaux = morceauxOptimisation(resume);
  const causes = [resume.ps?.cause, resume.fta?.cause].filter(Boolean).join(' ');
  const detail = resume.resultats_absents || resume.statut !== 'concluant' ? '' : `PS ${euros(resume.ps?.economie_eur)} · FTA ${euros(resume.fta?.economie_eur)}${causes ? ` — ${causes}` : ''}`;
  let contenu;
  if (resume.resultats_absents) contenu = 'Résultats supprimés — rapport seul';
  else if (resume.statut !== 'concluant') {
    contenu = (
      <span className="text-amber-700" title={(resume.motifs || []).join(' ')}>
        {resume.statut === 'non_verifie' ? 'Non vérifiée — relancer' : 'Non concluante'}
        <span className="block text-[9px] text-amber-600 truncate max-w-[120px]">{(resume.motifs || [])[0]}</span>
      </span>
    );
  }
  else if (!morceaux.ps && !morceaux.fta) contenu = 'Aucune proposition';
  else contenu = (
    <>
      {morceaux.ps && <span className="block whitespace-nowrap">{morceaux.ps}</span>}
      {morceaux.fta && <span className="block whitespace-nowrap">{morceaux.fta}</span>}
    </>
  );
  return (
    <>
      <td className="px-2 py-1 text-[9px] leading-tight text-gray-700" data-testid="turpe-optimisation" title={libelleOptimisation(resume)}>{contenu}</td>
      <td
        className={`px-2 py-1.5 text-[10px] text-right font-semibold whitespace-nowrap ${Number(resume.economie_totale_eur) > 0 ? 'text-emerald-700' : 'text-gray-500'}`}
        title={detail}
        data-testid="turpe-economie"
      >
        {euros(resume.economie_totale_eur)}
      </td>
    </>
  );
}

/** Économie totale et nombre de PDL analysés d'un compte, pour l'année choisie. */
export function totauxCompte(pdls, annee) {
  let analyses = 0;
  let economie = 0;
  let inconnu = false;
  for (const p of pdls) {
    if (p.turpe_analyses === null) { inconnu = true; continue; }
    const choix = choisirResume(p.turpe_analyses, annee);
    if (!choix || choix.autreAnnee || choix.resume.statut !== 'concluant') continue;
    analyses += 1;
    economie += Number(choix.resume.economie_totale_eur) || 0;
  }
  return { analyses, economie, inconnu };
}

export { euros };
